import React from "react";

import { FormProvider, useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { yupResolver } from "@hookform/resolvers/yup";
import { timezone } from "@/utils";
import ModalMotion, {
  AnimateDirection,
} from "@/components/Molecules/ModalMotion";
import Button from "@/components/Atoms/Button";

import NameInputV2 from "../RightSection/Form/Molecules/NameInput";
import PhoneInputV2 from "../RightSection/Form/Molecules/PhoneInput";
import CTASuccessView from "@/components/Molecules/CTASuccessView";
import EmailInputV2 from "../RightSection/Form/Molecules/EmailInput";
import CompanyNameInputV2 from "@/components/Organisms/FormInputsV2/CompanyNameInput";

import {
  ButtonContainer,
  FormContainer,
  FormWrapper,
  HeaderContainer,
  HeaderSubTitleTitle,
  HeaderTitle,
} from "./style";
import { corporateFormInputSchema } from "./schema";
import {
  postPackageSave as postNutritionCallbackData,
  saveLead,
} from "@/redux/LandingPageReducer";
import { DEFAULT_URL_ARGUMENTS } from "@/utils/Constants";

function CorporateForm({ handleCorporateFormView, defaultSlug }) {
  const dispatch = useDispatch();
  const countryCodes = useSelector((state) => state.countryCodeReducer);

  const { isLoading, error, success } = useSelector(
    (state) => state.nutritionPageReducer
  );

  const formMethods = useForm({
    mode: "onBlur",
    resolver: yupResolver(corporateFormInputSchema),
    defaultValues: {
      mobileCountryCode: "+91",
      mobile: "",
      name: "",
      companyName: "",
      email: "",
    },
  });
  const {
    handleSubmit,
    control,
    getValues,
    formState: { errors, isSubmitSuccessful },
  } = formMethods;

  const onBlur = () => {
    const { email, mobile, mobileCountryCode, name, companyName } = getValues();

    dispatch(
      saveLead({
        is_lead: true,
        name,
        phone_prefix: mobileCountryCode,
        phone: mobile,
        email,
        package_slug: defaultSlug,
        urls_arguments: DEFAULT_URL_ARGUMENTS,
        payment_mode: "",
        timezone,
        company_name: companyName,
      })
    );
  };

  const onSubmit = () => {
    const { email, mobile, mobileCountryCode, name, companyName } = getValues();

    let apiData = {
      is_callback_request: true,
      name,
      phone_prefix: mobileCountryCode,
      mobile: mobile,
      email,
      package_slug: defaultSlug,
      urls_arguments: DEFAULT_URL_ARGUMENTS,
      timezone: timezone,
      company_name: companyName,
    };
    dispatch(postNutritionCallbackData(apiData, defaultSlug, timezone)).then(
      () => {
        setTimeout(() => {
          handleCorporateFormView();
        }, 60000);
      }
    );
  };

  const HeaderView = (
    <HeaderContainer>
      <HeaderTitle variant="heading4">Explore our corporate plans</HeaderTitle>
      <HeaderSubTitleTitle variant="paragraph1">
        Leave your details here - we’ll call you right back!
      </HeaderSubTitleTitle>
    </HeaderContainer>
  );

  const FormView = (
    <FormContainer>
      <NameInputV2
        name={"name"}
        control={control}
        namePlaceholder="Your name"
        inputFontSize="14px"
        inputLineHeight="24px"
      />

      <PhoneInputV2
        control={control}
        name="mobile"
        countryCodeName="countryCode"
        countryCodes={countryCodes}
        mobilePlaceholder={"Your mobile"}
        inputFontSize="14px"
        inputLineHeight="24px"
        handleBlur={onBlur}
      />

      <EmailInputV2
        control={control}
        name="email"
        emailPlaceholder="Enter official email ID"
        inputFontSize="14px"
        inputLineHeight="24px"
        handleBlur={onBlur}
      />
      <CompanyNameInputV2
        companyNamePlaceholder="Company name"
        control={control}
        name="companyName"
        inputFontSize="14px"
        inputLineHeight="24px"
      />
    </FormContainer>
  );

  const ButtonView = (
    <ButtonContainer>
      <Button
        type="submit"
        style={{
          width: "226px",
          height: "56px",
        }}
        disabled={isLoading}
        isLoading={isLoading}
      >
        Request a callback
      </Button>
    </ButtonContainer>
  );

  return (
    <ModalMotion
      overlayClosable
      onClose={handleCorporateFormView}
      width={"512px"}
      animateDirection={AnimateDirection.CENTER}
      borderRadius={"16px"}
      showClose
      iconTopPosition={"20px"}
      iconRightPosition={"20px"}
      bodyPadding="48px 32px 32px 32px"
    >
      {(!isSubmitSuccessful || isLoading) && (
        <FormProvider {...formMethods}>
          <FormWrapper>
            <>
              {HeaderView}
              <form onSubmit={handleSubmit(onSubmit)}>
                {FormView}
                {ButtonView}
              </form>
            </>
          </FormWrapper>
        </FormProvider>
      )}
      {success && isSubmitSuccessful && <CTASuccessView />}
    </ModalMotion>
  );
}

export default CorporateForm;
